import React, { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

const rowsPerPageOptions = [
  { value: 5, label: '5 por página' },
  { value: 10, label: '10 por página' },
  { value: 20, label: '20 por página' },
  { value: 50, label: '50 por página' },
  { value: 100, label: '100 por página' },
  { value: -1, label: 'Todas' }
];

const DiaryActivitiesTable = ({ 
  activities = [], 
  onEdit, 
  onDelete, 
  isEditing = false 
}) => {
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesPerPage, setActivitiesPerPage] = useState(5);

  // Ordena as atividades por data de atualização/criação
  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      const dateA = new Date(a.date_updated || a.date_created);
      const dateB = new Date(b.date_updated || b.date_created);
      return dateB - dateA; // Ordem decrescente (mais recente primeiro)
    });
  }, [activities]);

  const getPaginatedActivities = () => {
    if (activitiesPerPage === -1) return sortedActivities;
    
    const startIndex = (activitiesPage - 1) * activitiesPerPage;
    const endIndex = startIndex + activitiesPerPage;
    return sortedActivities.slice(startIndex, endIndex);
  };

  const totalPages = useMemo(() => {
    if (activitiesPerPage === -1) return 1;
    return Math.ceil(sortedActivities.length / activitiesPerPage);
  }, [sortedActivities.length, activitiesPerPage]);

  const handleChangePage = (event, newPage) => {
    setActivitiesPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newValue = parseInt(event.target.value);
    setActivitiesPerPage(newValue);
    setActivitiesPage(1);
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Atividades Registradas
      </Typography>
      
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <FormControl variant="outlined" size="small">
          <InputLabel>Itens por página</InputLabel>
          <Select
            value={activitiesPerPage}
            onChange={handleChangeRowsPerPage}
            label="Itens por página"
          >
            {rowsPerPageOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="textSecondary">
          Total de atividades: {sortedActivities.length}
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Atividade</TableCell>
              <TableCell>Intensidade</TableCell>
              <TableCell>Informações Importantes</TableCell>
              {isEditing && <TableCell align="right">Ações</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {getPaginatedActivities().map((activity, index) => (
              <TableRow key={index}>
                <TableCell>{activity.activity?.name}</TableCell>
                <TableCell>{activity.intensity?.name}</TableCell>
                <TableCell>{activity.important_info}</TableCell>
                {isEditing && (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(activity)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(activity)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {sortedActivities.length === 0 && (
              <TableRow>
                <TableCell 
                  colSpan={isEditing ? 4 : 3} 
                  align="center"
                >
                  Nenhuma atividade registrada
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={activitiesPage}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Paper>
  );
};

export default DiaryActivitiesTable;
